import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiClient } from '../../../services/api';

/**
 * App-level registry of in-flight imports.
 *
 * The point of this living above the importer component is that the user can
 * leave the page.  Component state cannot survive a route change, let alone a
 * reload, so "am I still importing?" is answered by the server: the poll below
 * reads `/api/imports/active/`, which lists the organisation's queued and
 * processing `ImportBatch` rows.
 *
 * Scoped per organisation rather than per user, so a colleague's running
 * import is visible too — the worker is shared, and two large imports at once
 * is a thing worth seeing before you start the second one.
 */

export interface ActiveImport {
  batch_id: number;
  dataset: string;
  dataset_label: string;
  status_url: string;
  file_name: string;
  status: 'queued' | 'processing';
  row_count: number;
  processed: number;
  stage: string;
  started_at: string | null;
  started_by: string;
  is_mine: boolean;
  /**
   * The batch claims to be in flight but has stopped moving — a worker that
   * dies mid-run leaves it "processing" indefinitely. The backend has always
   * sent this; the indicators ignored it and span forever on work that was
   * not happening.
   */
  stalled: boolean;
}

/** An import that was running a moment ago and is now gone from the poll. */
export interface FinishedImport {
  batch_id: number;
  dataset: string;
  dataset_label: string;
  file_name: string;
  finished_at: number;
}

interface ImportJobContextValue {
  activeImports: ActiveImport[];
  /**
   * Imports that disappeared from the poll within the last few seconds.
   *
   * Without this, a finished import is announced only by its indicator
   * vanishing — the user has to notice an absence and infer success from it,
   * which is indistinguishable from the ticker having failed to load.
   */
  finishedImports: FinishedImport[];
  /** Nudge the poll immediately, e.g. straight after starting an import. */
  refresh: () => void;
  /**
   * Register interest while a component that cares is on screen.
   *
   * Returns an unsubscribe. Without this the provider wraps the whole
   * authenticated shell and polls forever, for every user, whether or not
   * they ever open an importer — a permanent baseline of requests to support
   * a ticker that is almost always showing nothing.
   */
  watch: () => () => void;
}

const ImportJobContext = createContext<ImportJobContextValue>({
  activeImports: [],
  finishedImports: [],
  refresh: () => {},
  // Outside a provider there is nothing to watch; the unsubscribe is a no-op
  // so a consumer can call it unconditionally on unmount.
  watch: () => () => {},
});

/** Idle cadence. Deliberately slow: this is a background awareness poll. */
const IDLE_POLL_MS = 30_000;
/** Faster cadence while something is actually running. */
const ACTIVE_POLL_MS = 6_000;
/** How long a finished import stays announced before it clears itself. */
const FINISHED_VISIBLE_MS = 12_000;

export const ImportJobProvider: React.FC<{
  /** Accepts either form: AuthContext stores a number, most callers a string. */
  organisationId: string | number | null;
  children: React.ReactNode;
}> = ({ organisationId: rawOrganisationId, children }) => {
  const organisationId =
    rawOrganisationId === null || rawOrganisationId === undefined
      ? null
      : String(rawOrganisationId);
  const [activeImports, setActiveImports] = useState<ActiveImport[]>([]);
  const [finishedImports, setFinishedImports] = useState<FinishedImport[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  // What the previous poll saw, so a disappearance can be detected.
  const seenRef = useRef<ActiveImport[]>([]);

  const fetchActive = useCallback(async () => {
    if (!organisationId) {
      setActiveImports([]);
      return [] as ActiveImport[];
    }
    try {
      const data = await apiClient.get<{ imports: ActiveImport[] }>(
        '/api/imports/active/',
        { organisation_id: organisationId },
      );
      const imports = data.imports || [];
      if (!cancelledRef.current) {
        // Anything present last time and absent now has finished. Stalled
        // jobs are excluded: they stop being reported once dismissed, and
        // announcing that as a completion would be a lie.
        const now = Date.now();
        const ids = new Set(imports.map((job) => job.batch_id));
        const justFinished = seenRef.current
          .filter((job) => !ids.has(job.batch_id) && !job.stalled)
          .map((job) => ({
            batch_id: job.batch_id,
            dataset: job.dataset,
            dataset_label: job.dataset_label,
            file_name: job.file_name,
            finished_at: now,
          }));

        if (justFinished.length > 0) {
          setFinishedImports((prev) => [...prev, ...justFinished]);
        }
        setFinishedImports((prev) =>
          prev.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
        );
        seenRef.current = imports;
        setActiveImports(imports);
      }
      return imports;
    } catch {
      // Silent: an awareness poll must never surface an error banner. If the
      // endpoint is unreachable the ticker simply shows nothing.
      return [] as ActiveImport[];
    }
  }, [organisationId]);

  // How many mounted components are actively interested. An importer on
  // screen counts; the navbar ticker alone does not, because with nothing
  // running it has nothing to show.
  const watchersRef = useRef(0);
  const wakeRef = useRef<((force?: boolean) => void) | null>(null);

  const watch = useCallback(() => {
    watchersRef.current += 1;
    // A newly mounted importer wants current state now, and restarts the loop
    // if it had gone quiet.
    wakeRef.current?.();
    return () => {
      watchersRef.current = Math.max(0, watchersRef.current - 1);
    };
  }, []);

  // Self-rescheduling loop rather than setInterval, so the cadence can follow
  // whether anything is actually running without tearing down the effect.
  useEffect(() => {
    cancelledRef.current = false;
    let stopped = false;

    let idle = false;
    // One fetch at a time. A forced tick that arrives mid-fetch asks for one
    // more pass rather than racing it, since the in-flight answer may predate
    // the import that prompted the nudge.
    let inFlight = false;
    let again = false;

    const tick = async () => {
      if (stopped) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      // A forced tick replaces whatever was scheduled, so the loop never runs
      // two timers at once.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      let imports = await fetchActive();
      while (again && !stopped) {
        again = false;
        imports = await fetchActive();
      }
      inFlight = false;
      if (stopped) return;

      // Nothing running and nobody looking: stop rather than idle forever.
      // One fetch per page load answers "is anything of mine in flight?", and
      // anything that starts after that goes through refresh() or watch().
      if (imports.length === 0 && watchersRef.current === 0) {
        idle = true;
        return;
      }
      idle = false;
      timerRef.current = setTimeout(
        tick,
        imports.length > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS,
      );
    };

    // `force` fetches now even when the loop is already running. Without it a
    // nudge was ignored whenever an importer was on screen (the loop is then
    // sitting in its 30-second idle wait, not stopped), so a freshly started
    // import stayed off the ticker for up to half a minute — and one that
    // finished inside that window never appeared, nor was announced.
    wakeRef.current = (force = false) => {
      if (stopped) return;
      if (!idle && !force) return;
      idle = false;
      void tick();
    };

    void tick();

    return () => {
      stopped = true;
      cancelledRef.current = true;
      wakeRef.current = null;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchActive]);

  const refresh = useCallback(() => {
    // Fetches immediately, restarting the loop if it had gone quiet —
    // starting an import is exactly when polling needs to resume.
    if (wakeRef.current) wakeRef.current(true);
    else void fetchActive();
  }, [fetchActive]);

  // A finished announcement must clear itself even when nothing is running,
  // because the poll drops to its idle cadence the moment the last job ends.
  useEffect(() => {
    if (finishedImports.length === 0) return;
    const timer = setTimeout(() => {
      const now = Date.now();
      setFinishedImports((prev) =>
        prev.filter((job) => now - job.finished_at < FINISHED_VISIBLE_MS),
      );
    }, FINISHED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [finishedImports]);

  const value = useMemo(
    () => ({ activeImports, finishedImports, refresh, watch }),
    [activeImports, finishedImports, refresh, watch],
  );

  return (
    <ImportJobContext.Provider value={value}>{children}</ImportJobContext.Provider>
  );
};

export function useImportJobs(): ImportJobContextValue {
  return useContext(ImportJobContext);
}

export default ImportJobContext;
